import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, Wifi, WifiOff, AlertCircle, 
  RefreshCw, Settings, Activity, ChevronDown, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Devices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchDevices();
  }, [page, statusFilter]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${BACKEND_URL}/api/devices?page=${page}&page_size=10`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDevices(response.data.data.devices);
        setTotalPages(response.data.data.total_pages);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (deviceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const handleToggleService = async (deviceId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const operation = currentStatus ? 'disable_service' : 'enable_service';
      
      await axios.post(
        `${BACKEND_URL}/api/devices/${deviceId}/operations`,
        { operation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh device list
      await fetchDevices();
    } catch (error) {
      console.error('Error toggling service:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      online: { 
        bg: 'bg-green-50', 
        border: 'border-green-200',
        text: 'text-green-700', 
        dot: 'bg-green-500',
        label: 'Online' 
      },
      offline: { 
        bg: 'bg-gray-50', 
        border: 'border-gray-200',
        text: 'text-gray-700', 
        dot: 'bg-gray-400',
        label: 'Offline' 
      },
      error: { 
        bg: 'bg-red-50', 
        border: 'border-red-200',
        text: 'text-red-700', 
        dot: 'bg-red-500',
        label: 'Error' 
      }
    };

    const config = statusConfig[status] || statusConfig.offline;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border ${config.bg} ${config.border} ${config.text}`}>
        <span className={`w-2 h-2 rounded-full ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = 
      device.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.mac_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Device Management</h1>
            <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">Manage CPE devices with ACS/TR-069</p>
          </div>
          <button
            onClick={() => navigate('/devices/register')}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus size={18} />
            Register Device
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide dark:text-slate-400">Total Devices</p>
                <p className="text-3xl font-bold text-gray-900 mt-2 dark:text-slate-100">{devices.length}</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg dark:bg-indigo-900/30">
                <Activity className="text-indigo-600 dark:text-indigo-400" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide dark:text-slate-400">Online</p>
                <p className="text-3xl font-bold text-green-600 mt-2 dark:text-green-400">
                  {devices.filter(d => d.status === 'online').length}
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg dark:bg-green-900/30">
                <Wifi className="text-green-600 dark:text-green-400" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide dark:text-slate-400">Offline</p>
                <p className="text-3xl font-bold text-gray-600 mt-2 dark:text-slate-300">
                  {devices.filter(d => d.status === 'offline').length}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg dark:bg-slate-900">
                <WifiOff className="text-gray-600 dark:text-slate-300" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide dark:text-slate-400">Error</p>
                <p className="text-3xl font-bold text-red-600 mt-2 dark:text-red-400">
                  {devices.filter(d => d.status === 'error').length}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg dark:bg-red-900/30">
                <AlertCircle className="text-red-600 dark:text-red-400" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by serial number, model, or MAC address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:border-slate-600 dark:bg-slate-800"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="error">Error</option>
            </select>

            <button
              onClick={fetchDevices}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium dark:border-slate-600 dark:hover:bg-slate-700/50"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Devices Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center dark:bg-slate-800 dark:border-slate-700">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 text-sm dark:text-slate-300">Loading devices...</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-8 dark:text-slate-300">
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Device
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Customer Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      WAN IP
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Last Seen
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Service
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filteredDevices.map((device) => (
                    <React.Fragment key={device.id}>
                      <tr className="hover:bg-gray-50 transition-colors dark:hover:bg-slate-700/50">
                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(device.id);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {expandedRows[device.id] ? 
                              <ChevronDown size={18} /> : 
                              <ChevronRight size={18} />
                            }
                          </button>
                        </td>
                        <td 
                          className="px-6 py-4 cursor-pointer"
                          onClick={() => navigate(`/devices/${device.id}`)}
                        >
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">{device.model}</div>
                            <div className="text-xs text-gray-500 mt-0.5 dark:text-slate-400">SN: {device.serial_number}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{device.mac_address}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {device.customer_name ? (
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{device.customer_name}</span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Unassigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(device.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 font-mono dark:text-slate-100">
                            {device.wan_ip || <span className="text-gray-400">-</span>}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600 dark:text-slate-300">
                            {device.last_seen ? new Date(device.last_seen).toLocaleString('id-ID', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : <span className="text-gray-400">-</span>}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                            device.service_enabled 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-gray-50 text-gray-700 border border-gray-200'
                          }`}>
                            {device.service_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {device.service_enabled ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleService(device.id, true);
                                }}
                                className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                              >
                                Disable
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleService(device.id, false);
                                }}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                              >
                                Enable
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/devices/${device.id}`);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors dark:hover:bg-indigo-900/30"
                            >
                              <Settings size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Row */}
                      {expandedRows[device.id] && (
                        <tr className="bg-gray-50 dark:bg-slate-900">
                          <td colSpan="8" className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 font-medium dark:text-slate-400">Firmware:</span>
                                <span className="ml-2 text-gray-900 dark:text-slate-100">{device.firmware_version || '-'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium dark:text-slate-400">Customer ID:</span>
                                <span className="ml-2 text-gray-900 dark:text-slate-100">{device.customer_id || 'Unassigned'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium dark:text-slate-400">Serial Number:</span>
                                <span className="ml-2 text-gray-900 font-mono text-xs dark:text-slate-100">{device.serial_number}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium dark:text-slate-400">MAC Address:</span>
                                <span className="ml-2 text-gray-900 font-mono text-xs dark:text-slate-100">{device.mac_address}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredDevices.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-16 text-center">
                  <WifiOff className="mx-auto text-gray-300 mb-3" size={48} />
                  <p className="text-gray-500 text-sm dark:text-slate-400">No devices found</p>
                  <p className="text-gray-400 text-xs mt-1">Try adjusting your search or filter criteria</p>
                </td>
              </tr>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-between dark:bg-slate-800 dark:border-slate-700">
              <div className="text-sm text-gray-600 dark:text-slate-300">
                Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:border-slate-600 dark:hover:bg-slate-700/50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:border-slate-600 dark:hover:bg-slate-700/50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Devices;
